import { useEffect } from "react";
import { Drawer as DrawerPrimitive } from "vaul";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { trackEvent } from "@/lib/analytics";

export type SocialPlatform = "tiktok" | "instagram" | "pinterest" | "facebook";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectPlatform: (platform: SocialPlatform) => void;
}

interface PlatformSpec {
  id: SocialPlatform;
  label: string;
  icon: string;
  bg: string;
}

const PLATFORMS: PlatformSpec[] = [
  { id: "tiktok", label: "TikTok", icon: "ti-brand-tiktok", bg: "#000000" },
  {
    id: "instagram",
    label: "Instagram",
    icon: "ti-brand-instagram",
    bg: "linear-gradient(135deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%)",
  },
  { id: "pinterest", label: "Pinterest", icon: "ti-brand-pinterest", bg: "#E60023" },
  { id: "facebook", label: "Facebook", icon: "ti-brand-facebook", bg: "#1877F2" },
];

export default function SocialPlatformSheet({ open, onOpenChange, onSelectPlatform }: Props) {
  useEffect(() => {
    if (open) trackEvent("social_sheet_opened");
  }, [open]);

  const handlePick = (p: PlatformSpec) => {
    void triggerHaptic("selection");
    trackEvent("social_platform_selected", { platform: p.id });
    onSelectPlatform(p.id);
    onOpenChange(false);
  };

  return (
    <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className="fixed inset-0 z-[60]"
          style={{ background: "rgba(44,40,32,0.4)" }}
        />
        <DrawerPrimitive.Content
          className="fixed inset-x-0 bottom-0 z-[61] flex flex-col"
          style={{
            background: "#FAECE7",
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            paddingTop: 12,
            paddingLeft: 20,
            paddingRight: 20,
            paddingBottom: "calc(28px + var(--safe-bottom))",
            outline: "none",
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 36,
              height: 5,
              borderRadius: 999,
              background: "#D4CCC0",
              margin: "0 auto 16px",
            }}
          />
          <DrawerPrimitive.Title
            style={{
              fontSize: 22,
              lineHeight: "28px",
              fontWeight: 500,
              color: "#2C2820",
              letterSpacing: "-0.4px",
              margin: 0,
            }}
          >
            Where did you see the look?
          </DrawerPrimitive.Title>
          <DrawerPrimitive.Description
            className="l2f-type-secondary"
            style={{
              color: "#6B6560",
              marginTop: 6,
              marginBottom: 22,
            }}
          >
            Pick the platform you're sharing from
          </DrawerPrimitive.Description>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            {PLATFORMS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handlePick(p)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#FFFCF8",
                  border: "0.5px solid #E8E2D9",
                  borderRadius: 16,
                  padding: "22px 16px",
                  cursor: "pointer",
                  transition: "transform 120ms ease",
                }}
                onPointerDown={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.97)";
                }}
                onPointerUp={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
                }}
                onPointerLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 11,
                    background: p.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className={`ti ${p.icon}`} style={{ fontSize: 24, color: "#FFFFFF" }} />
                </div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#2C2820",
                    marginTop: 12,
                  }}
                >
                  {p.label}
                </div>
              </button>
            ))}
          </div>

          <div
            className="l2f-type-caption"
            style={{
              color: "#9B958E",
              textAlign: "center",
              marginTop: 22,
            }}
          >
            Like2Fit appears in your share menu
          </div>
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}
